import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { Toast, ToastService } from '../admin/services/toast.service';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-public-shadcn-toaster',
    encapsulation: ViewEncapsulation.None,
    template: `
    <div class="sonner-toaster">
      @for (t of toasts; track t; let i = $index) {
        <div
          class="sonner-toast"
          [ngClass]="'sonner-toast--' + t.variant"
          [class.sonner-toast--removing]="t.removing"
          [style.--index]="toasts.length - 1 - i">
          <div class="sonner-toast__icon">
            @switch (t.variant) {
              @case ('success') {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
              }
              @case ('error') {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>
              }
              @case ('warning') {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
              }
              @case ('info') {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
              }
              @default {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              }
            }
          </div>
          <div class="sonner-toast__message">{{ t.message }}</div>
          <button class="sonner-toast__close" (click)="dismiss(t)">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>
      }
    </div>
    `,
    styles: [`
    .sonner-toaster {
      position: fixed;
      bottom: 1.5rem;
      right: 1.5rem;
      z-index: 9999;
      display: flex;
      flex-direction: column-reverse;
      gap: 0.5rem;
      pointer-events: none;
    }

    .sonner-toast {
      pointer-events: auto;
      display: flex;
      align-items: center;
      gap: 0.625rem;
      min-width: 20rem;
      max-width: 26rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      border: 1px solid var(--border);
      background: var(--card, #fff);
      color: var(--foreground, #0a0a0a);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06);
      font-family: inherit;
      font-size: 0.875rem;
      line-height: 1.35;
      animation: sonner-slide-in 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
    }

    .sonner-toast--removing {
      animation: sonner-slide-out 0.2s cubic-bezier(0.06, 0.71, 0.55, 1) forwards;
    }

    @keyframes sonner-slide-in {
      from { opacity: 0; transform: translateY(100%) scale(0.96); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }

    @keyframes sonner-slide-out {
      from { opacity: 1; transform: translateX(0); }
      to   { opacity: 0; transform: translateX(100%); }
    }

    .sonner-toast__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 1.125rem;
      height: 1.125rem;
    }
    .sonner-toast__icon svg {
      width: 1.125rem;
      height: 1.125rem;
    }
    .sonner-toast--success .sonner-toast__icon { color: #16a34a; }
    .sonner-toast--error .sonner-toast__icon   { color: #dc2626; }
    .sonner-toast--warning .sonner-toast__icon { color: #d97706; }
    .sonner-toast--info .sonner-toast__icon    { color: #2563eb; }

    .sonner-toast__message {
      flex: 1;
      font-weight: 500;
    }

    .sonner-toast__close {
      all: unset;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 0.25rem;
      cursor: pointer;
      opacity: 0.5;
      transition: opacity 0.15s;
    }
    .sonner-toast__close:hover {
      opacity: 1;
    }
    .sonner-toast__close svg {
      width: 0.875rem;
      height: 0.875rem;
    }
  `],
    imports: [NgClass]
})
export class PublicShadcnToasterComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];
  private sub?: Subscription;
  private timers = new Map<number, any>();

  constructor(
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.sub = this.toastService.toast$.subscribe((t) => {
      this.toasts.push(t);
      this.timers.set(t.id, setTimeout(() => this.dismiss(t), t.duration));
      this.cdr.markForCheck();
    });
  }

  dismiss(t: Toast) {
    clearTimeout(this.timers.get(t.id));
    this.timers.delete(t.id);
    t.removing = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.toasts = this.toasts.filter((x) => x.id !== t.id);
      this.cdr.markForCheck();
    }, 200);
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.timers.forEach((timer) => clearTimeout(timer));
  }
}
